/**
 * /reportes — Resumen de ventas, IVA recaudado, retenciones por período.
 *
 * Usa endpoint backend /manual-invoices/summary con SQL aggregate.
 * Escala a millones de facturas.
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Loader2, TrendingUp } from 'lucide-react'

import { AppShell } from '@/components/layout/app-shell'
import { useAuthContext } from '@/hooks/use-auth-context'
import { invoiceApi } from '@/lib/api'

const COP = new Intl.NumberFormat('es-CO', {
  style: 'currency',
  currency: 'COP',
  maximumFractionDigits: 0,
})

function firstDayOfMonth(d: Date) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`
}

function lastDayOfMonth(d: Date) {
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0)
  return `${last.getFullYear()}-${String(last.getMonth() + 1).padStart(2, '0')}-${String(
    last.getDate(),
  ).padStart(2, '0')}`
}

export function ReportsPage() {
  const { activeOrgId, isLoading: authLoading } = useAuthContext()
  const today = new Date()
  const [from, setFrom] = useState(firstDayOfMonth(today))
  const [to, setTo] = useState(lastDayOfMonth(today))

  const summary = useQuery({
    queryKey: ['report-summary', activeOrgId, from, to],
    queryFn: () => invoiceApi.summary(activeOrgId!, from, to),
    enabled: !!activeOrgId,
  })

  if (authLoading) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 p-6 text-sm text-fg-muted">
          <Loader2 className="h-4 w-4 animate-spin" /> Cargando contexto...
        </div>
      </AppShell>
    )
  }

  if (!activeOrgId) {
    return (
      <AppShell>
        <div className="rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
          Sin organización activa. <a href="/login" className="underline">Iniciar sesión</a>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-5xl">
        <h1 className="text-3xl font-bold">Reportes</h1>
        <p className="mt-1 text-sm text-fg-secondary">
          Resumen de ventas, IVA recaudado y estado DIAN por período.
          Agregación SQL backend (escala a millones de docs).
        </p>

        <div className="mt-6 flex flex-wrap items-end gap-4 rounded-xl border border-border bg-bg-surface p-4">
          <div>
            <label className="block text-xs font-medium" htmlFor="from">
              Desde
            </label>
            <input
              id="from"
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className="mt-1 rounded-md border border-border bg-bg-app px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-xs font-medium" htmlFor="to">
              Hasta
            </label>
            <input
              id="to"
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className="mt-1 rounded-md border border-border bg-bg-app px-3 py-1.5 text-sm focus:border-brand-500 focus:outline-none"
            />
          </div>
        </div>

        {summary.isLoading && (
          <div className="mt-6 flex items-center gap-2 text-sm text-fg-muted">
            <Loader2 className="h-4 w-4 animate-spin" /> Calculando...
          </div>
        )}

        {summary.isError && (
          <div className="mt-6 rounded-xl border border-error/30 bg-error-tint p-6 text-sm text-error-fg">
            Error: {(summary.error as Error).message}
          </div>
        )}

        {summary.data && (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <KPI
                label="Ventas brutas (facturas)"
                value={COP.format(parseFloat(summary.data.totals.sales_gross_invoice))}
                accent="brand"
              />
              <KPI
                label="Notas crédito"
                value={`-${COP.format(parseFloat(summary.data.totals.credit_note_total))}`}
                accent="warning"
              />
              <KPI
                label="Notas débito"
                value={`+${COP.format(parseFloat(summary.data.totals.debit_note_total))}`}
                accent="info"
              />
              <KPI
                label="Ventas netas"
                value={COP.format(parseFloat(summary.data.totals.sales_net))}
                accent="success"
              />
              <KPI
                label="IVA total"
                value={COP.format(parseFloat(summary.data.totals.tax_iva_total))}
                accent="info"
              />
              <KPI
                label="INC total"
                value={COP.format(parseFloat(summary.data.totals.tax_inc_total))}
                accent="info"
              />
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              <BadgeCount label="Borradores" count={summary.data.by_status.draft} kind="muted" />
              <BadgeCount label="Enviando" count={summary.data.by_status.submitting} kind="info" />
              <BadgeCount
                label="Aceptadas DIAN"
                count={summary.data.by_status.accepted}
                kind="success"
              />
              <BadgeCount
                label="Rechazadas DIAN"
                count={summary.data.by_status.rejected}
                kind="error"
              />
              <BadgeCount label="Fallidas" count={summary.data.by_status.failed} kind="warning" />
            </div>

            <div className="mt-6 grid gap-4 sm:grid-cols-3">
              <BadgeCount
                label="Facturas"
                count={summary.data.by_doc_type.invoice}
                kind="muted"
              />
              <BadgeCount
                label="Notas crédito"
                count={summary.data.by_doc_type.credit_note}
                kind="muted"
              />
              <BadgeCount
                label="Notas débito"
                count={summary.data.by_doc_type.debit_note}
                kind="muted"
              />
            </div>

            <div className="mt-6 rounded-md border border-border bg-bg-elevated p-4 text-xs text-fg-secondary">
              <p>
                <TrendingUp className="mr-1 inline h-3.5 w-3.5" />
                Período: <strong>{summary.data.from || 'inicio'}</strong> →{' '}
                <strong>{summary.data.to || 'hoy'}</strong>. Total documentos:{' '}
                <strong>{summary.data.totals.doc_count}</strong>. Ventas netas =
                facturas − notas crédito + notas débito. Excluye archivadas.
              </p>
            </div>
          </>
        )}
      </div>
    </AppShell>
  )
}

function KPI({
  label,
  value,
  accent,
}: {
  label: string
  value: string
  accent: 'brand' | 'success' | 'warning' | 'info' | 'muted'
}) {
  const ring: Record<string, string> = {
    brand: 'border-brand-500/40 bg-brand-50/30',
    success: 'border-success/30 bg-success-tint',
    warning: 'border-warning/30 bg-warning-tint',
    info: 'border-info/30 bg-info/10',
    muted: 'border-border bg-bg-surface',
  }
  return (
    <div className={`rounded-xl border-2 ${ring[accent]} p-4`}>
      <p className="text-xs font-medium uppercase tracking-wide text-fg-muted">{label}</p>
      <p className="mt-2 font-mono text-xl font-bold">{value}</p>
    </div>
  )
}

function BadgeCount({
  label,
  count,
  kind,
}: {
  label: string
  count: number
  kind: 'success' | 'error' | 'warning' | 'info' | 'muted'
}) {
  const cls: Record<string, string> = {
    success: 'border-success/30 bg-success-tint text-success-fg',
    error: 'border-error/30 bg-error-tint text-error-fg',
    warning: 'border-warning/30 bg-warning-tint text-warning-fg',
    info: 'border-info/30 bg-info/10 text-info',
    muted: 'border-border bg-bg-surface text-fg-secondary',
  }
  return (
    <div className={`rounded-xl border-2 ${cls[kind]} p-4 text-center`}>
      <p className="text-xs font-medium">{label}</p>
      <p className="mt-1 text-2xl font-bold">{count}</p>
    </div>
  )
}
